import { fmtEur, fmtNumber } from '@/i18n'
/**
 * Chart tokens.
 *
 * Both modes validated with the dataviz validator, not eyeballed:
 *
 *   light (#2a78d6, #eb6834, #1baf7a) on #fcfcfb — ALL CHECKS PASS
 *     worst adjacent CVD ΔE 9.2 (deutan), normal-vision ΔE 27.6
 *     WARN: aqua sits at 2.74:1 against the light surface, below 3:1
 *
 *   dark (#3987e5, #d95926, #199e70) on #1a1a19 — ALL CHECKS PASS
 *
 * The light-mode contrast WARN is not dismissable: it obliges **relief** — visible
 * direct labels or a table view. Every chart here ships value labels AND a table
 * toggle, which satisfies it. If you add a chart, keep that.
 *
 * Series colours are for MARKS ONLY. Labels, values, legends and axis text wear the
 * text tokens below — a light hue like aqua is illegible as text on the surface, and
 * identity comes from the coloured mark beside the text.
 *
 * Brand green is deliberately absent: it belongs to the UI (nav, buttons, focus) and
 * must never read as a data series.
 */

export const SERIES = {
  /** Quantities — metres, counts. */
  primary: 'var(--viz-series-1)',
  /** A second category when one is genuinely needed (MIH vs SUB). */
  secondary: 'var(--viz-series-2)',
  /** Money. */
  money: 'var(--viz-series-3)',
} as const

/**
 * Status colours are reserved: never reused as "series 4", and always shipped with
 * an icon and a label so state is never carried by colour alone.
 */
export const STATUS = {
  good: 'var(--viz-good)',
  warning: 'var(--viz-warning)',
  serious: 'var(--viz-serious)',
  critical: 'var(--viz-critical)',
} as const

// Number formatting follows the chosen language (i18n): 1.234 € in de/cnr/bs/sr, €1,234 in en.
const nf = { format: (n: number) => fmtNumber(n) }
const nf1 = { format: (n: number) => fmtNumber(n, 1) }
const eur = { format: (n: number) => fmtEur(n) }

export function formatValue(value: number, unit: string): string {
  if (unit === 'EUR') return eur.format(value)
  if (unit === 'm') return `${nf.format(value)} m`
  if (unit === 'h') return `${nf.format(value)} h`
  return nf.format(value)
}

/** Compact form for axis ticks and tight labels — keeps the unit so a bare number is never ambiguous. */
export function formatCompact(value: number, unit?: string): string {
  const abs = Math.abs(value)
  const s =
    abs >= 1_000_000 ? `${nf1.format(value / 1_000_000)}M`
    : abs >= 1_000 ? `${nf1.format(value / 1_000)}k`
    : nf.format(value)
  if (unit === 'EUR') return `€${s}`
  if (unit === 'm') return `${s} m`
  if (unit === 'h') return `${s} h`
  return s
}
